<template>
  <div class="home">
    <div class="lang-toggle">
      <button @click="toggleLanguage" class="lang-button glass-panel">
        {{ currentLocale === "pt" ? "EN" : "PT" }}
      </button>
    </div>

    <section id="home" class="hero">
      <div class="hero-split">
        <div class="hero-side hero-side--alvine" @mouseenter="activeSide = 'alvine'" :class="{ 'active': activeSide === 'alvine' }">
          <div class="hero-overlay"></div>
          <div class="hero__content">
            <div class="logo logo--alvine animated bounceIn"></div>
            <h2 class="hero-subtitle fade-in">{{ $t("sections.alvine.subtitle") }}</h2>
            <button @click="scrollToSection('alvine')" class="btn btn-alvine fade-in">
              {{ $t("hero.discover") }}
            </button>
          </div>
        </div>

        <div class="hero-side hero-side--munk" @mouseenter="activeSide = 'munk'" :class="{ 'active': activeSide === 'munk' }">
          <div class="hero-overlay"></div>
          <div class="hero__content">
            <div class="logo logo--munk animated bounceIn"></div>
            <h2 class="hero-subtitle fade-in">{{ $t("sections.munk.subtitle") }}</h2>
            <button @click="scrollToSection('munk')" class="btn btn-munk fade-in">
              {{ $t("hero.discover") }}
            </button>
          </div>
        </div>
      </div>

      <div class="scroll-indicator" @click="scrollToContent">
        <i class="fas fa-chevron-down animated pulse infinite"></i>
      </div>
    </section>

    <div class="split-layout">
      <!-- Alvine Section -->
      <section id="alvine" class="split-layout__alvine blue-theme">
        <div class="container brand-section">
          <div class="section-header animated fadeIn">
            <div class="logo logo--alvine logo--medium"></div>
            <p class="section-subtitle">{{ $t("sections.alvine.subtitle") }}</p>
          </div>

          <div class="glass-panel content-card animated fadeIn">
            <p class="section-description">{{ $t("sections.alvine.description") }}</p>
          </div>

          <div class="chipmunks-wrapper">
            <div class="chipmunk-character alvin float">
              <div class="character-image"></div>
              <h3>Alvin</h3>
            </div>
            <div class="chipmunk-character simon float" style="animation-delay: 1s">
              <div class="character-image"></div>
              <h3>Simon</h3>
            </div>
            <div class="chipmunk-character theodore float" style="animation-delay: 2s">
              <div class="character-image"></div>
              <h3>Theodore</h3>
            </div>
          </div>

          <div class="social-links animated fadeIn">
            <a href="https://instagram.com/alvineosesquilosbr" target="_blank" class="social-icon social-icon--instagram">
              <i class="fab fa-instagram"></i>
            </a>
            <a href="https://tiktok.com/@alvineosesquilosbr" target="_blank" class="social-icon social-icon--tiktok">
              <i class="fab fa-tiktok"></i>
            </a>
            <a href="https://youtube.com/alvineosesquilosbr" target="_blank" class="social-icon social-icon--youtube">
              <i class="fab fa-youtube"></i>
            </a>
          </div>

          <div class="social-feed-section">
            <h3 class="section-title animated fadeIn">{{ $t("portfolio.recommended") }}</h3>

            <div v-if="isLoading" class="loading-container">
              <div class="loading-spinner"></div>
            </div>

            <div v-else class="social-grid">
              <!-- Instagram Card -->
              <div class="social-feed-card glass-panel">
                <div class="card-header">
                  <i class="fab fa-instagram"></i>
                  <h4>{{ $t("portfolio.instagram") }}</h4>
                </div>
                <div class="card-content custom-embed-container">
                  <a :href="alvinePosts.instagram?.link" target="_blank" rel="noopener" class="custom-social-embed">
                    <img :src="alvinePosts.instagram?.imageUrl" alt="Instagram post" class="embed-thumbnail" />
                    <div class="embed-overlay">
                      <div class="embed-icon"><i class="fab fa-instagram"></i></div>
                    </div>
                  </a>
                </div>
              </div>

              <!-- TikTok Card -->
              <div class="social-feed-card glass-panel tiktok-card">
                <div class="card-header">
                  <i class="fab fa-tiktok"></i>
                  <h4>{{ $t("portfolio.tiktok") }}</h4>
                </div>
                <div class="card-content custom-embed-container">
                  <a :href="alvinePosts.tiktok?.link" target="_blank" rel="noopener" class="custom-social-embed">
                    <img :src="alvinePosts.tiktok?.imageUrl" alt="TikTok video" class="embed-thumbnail" />
                    <div class="embed-overlay">
                      <div class="embed-icon"><i class="fab fa-tiktok"></i></div>
                      <div class="play-button"><i class="fas fa-play"></i></div>
                    </div>
                  </a>
                </div>
              </div>

              <!-- YouTube Card -->
              <div class="social-feed-card glass-panel">
                <div class="card-header">
                  <i class="fab fa-youtube"></i>
                  <h4>{{ $t("portfolio.youtube") }}</h4>
                </div>
                <div class="card-content custom-embed-container">
                  <a :href="alvinePosts.youtube?.link" target="_blank" rel="noopener" class="custom-social-embed">
                    <img :src="alvinePosts.youtube?.imageUrl" alt="YouTube video" class="embed-thumbnail" />
                    <div class="embed-overlay">
                      <div class="embed-icon"><i class="fab fa-youtube"></i></div>
                      <div class="play-button"><i class="fas fa-play"></i></div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div class="section-contact">
            <ContactForm brand="alvine" />
          </div>
        </div>
      </section>

      <!-- Munk Section -->
      <section id="munk" class="split-layout__munk green-theme">
        <div class="container brand-section">
          <div class="section-header animated fadeIn">
            <div class="logo logo--munk logo--medium"></div>
            <p class="section-subtitle">{{ $t("sections.munk.subtitle") }}</p>
          </div>

          <div class="glass-panel content-card animated fadeIn">
            <p class="section-description">{{ $t("sections.munk.description") }}</p>
          </div>

          <div class="chipmunks-wrapper">
            <div class="chipmunk-character alvin-global float">
              <div class="character-image"></div>
              <h3>Alvin</h3>
            </div>
            <div class="chipmunk-character simon-global float" style="animation-delay: 1s">
              <div class="character-image"></div>
              <h3>Simon</h3>
            </div>
            <div class="chipmunk-character theodore-global float" style="animation-delay: 2s">
              <div class="character-image"></div>
              <h3>Theodore</h3>
            </div>
          </div>

          <div class="social-links animated fadeIn">
            <a href="https://instagram.com/munkyourself" target="_blank" class="social-icon social-icon--instagram">
              <i class="fab fa-instagram"></i>
            </a>
            <a href="https://tiktok.com/@munkyourself" target="_blank" class="social-icon social-icon--tiktok">
              <i class="fab fa-tiktok"></i>
            </a>
            <a href="https://youtube.com/munkyourself" target="_blank" class="social-icon social-icon--youtube">
              <i class="fab fa-youtube"></i>
            </a>
          </div>

          <div class="social-feed-section">
            <h3 class="section-title animated fadeIn">{{ $t("portfolio.recommended") }}</h3>

            <div v-if="isLoading" class="loading-container">
              <div class="loading-spinner"></div>
            </div>

            <div v-else class="social-grid">
              <!-- Instagram Card -->
              <div class="social-feed-card glass-panel">
                <div class="card-header">
                  <i class="fab fa-instagram"></i>
                  <h4>{{ $t("portfolio.instagram") }}</h4>
                </div>
                <div class="card-content custom-embed-container">
                  <a :href="munkPosts.instagram?.link" target="_blank" rel="noopener" class="custom-social-embed">
                    <img :src="munkPosts.instagram?.imageUrl" alt="Instagram post" class="embed-thumbnail" />
                    <div class="embed-overlay">
                      <div class="embed-icon"><i class="fab fa-instagram"></i></div>
                    </div>
                  </a>
                </div>
              </div>

              <!-- TikTok Card -->
              <div class="social-feed-card glass-panel tiktok-card">
                <div class="card-header">
                  <i class="fab fa-tiktok"></i>
                  <h4>{{ $t("portfolio.tiktok") }}</h4>
                </div>
                <div class="card-content custom-embed-container">
                  <a :href="munkPosts.tiktok?.link" target="_blank" rel="noopener" class="custom-social-embed">
                    <img :src="munkPosts.tiktok?.imageUrl" alt="TikTok video" class="embed-thumbnail" />
                    <div class="embed-overlay">
                      <div class="embed-icon"><i class="fab fa-tiktok"></i></div>
                      <div class="play-button"><i class="fas fa-play"></i></div>
                    </div>
                  </a>
                </div>
              </div>

              <!-- YouTube Card -->
              <div class="social-feed-card glass-panel">
                <div class="card-header">
                  <i class="fab fa-youtube"></i>
                  <h4>{{ $t("portfolio.youtube") }}</h4>
                </div>
                <div class="card-content custom-embed-container">
                  <a :href="munkPosts.youtube?.link" target="_blank" rel="noopener" class="custom-social-embed">
                    <img :src="munkPosts.youtube?.imageUrl" alt="YouTube video" class="embed-thumbnail" />
                    <div class="embed-overlay">
                      <div class="embed-icon"><i class="fab fa-youtube"></i></div>
                      <div class="play-button"><i class="fas fa-play"></i></div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="section-contact">
            <ContactForm brand="munk" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ContactForm from "../components/ContactForm.vue";
import { fetchRecommendedPosts } from "../services/socialMedia.js";

export default {
  name: "Home",
  components: {
    ContactForm,
  },
  data() {
    return {
      isLoading: true,
      activeSide: null, // 'alvine' or 'munk'
      alvinePosts: {
        instagram: null,
        tiktok: null,
        youtube: null,
      },
      munkPosts: {
        instagram: null,
        tiktok: null,
        youtube: null,
      },
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
  },
  async mounted() {
    this.initScrollAnimations();
    await this.loadRecommendedPosts();
  },

  methods: {
    toggleLanguage() {
      this.$i18n.locale = this.currentLocale === "pt" ? "en" : "pt";
    },

    scrollToContent() {
      const splitLayout = document.querySelector(".split-layout");
      if (splitLayout) {
        splitLayout.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },

    scrollToSection(id) {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    },

    async loadRecommendedPosts() {
      try {
        this.isLoading = true;
        const [alvineData, munkData] = await Promise.all([
          fetchRecommendedPosts("alvine"),
          fetchRecommendedPosts("munk"),
        ]);
        this.alvinePosts = alvineData;
        this.munkPosts = munkData;
        this.isLoading = false;
        this.$nextTick(() => {
          this.initScrollAnimations();
        });
      } catch (error) {
        console.error("Erro ao carregar posts recomendados:", error);
        this.isLoading = false;
      }
    },

    initScrollAnimations() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      document
        .querySelectorAll(
          ".section-header, .content-card, .social-links, .chipmunk-character, .social-feed-card"
        )
        .forEach((el) => observer.observe(el));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/variables";
@import "../assets/styles/mixins";

.home {
  padding-top: 0;
}

.lang-toggle {
  position: fixed;
  top: 30px;
  right: 30px;
  z-index: 1000;

  .lang-button {
    padding: $spacing-xs $spacing-md;
    color: $white;
    font-weight: bold;
    cursor: pointer;
    transition: all $transition-normal;
    border-radius: 50px;

    &:hover {
      background: $white;
      color: $black;
    }
  }
}

.hero {
  height: 100vh;
  position: relative;
  overflow: hidden;

  .hero-split {
    display: flex;
    height: 100%;
    width: 100%;
    
    @include respond-to(md) {
      flex-direction: row;
    }
    flex-direction: column; // Mobile default
  }

  .hero-side {
    flex: 1;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all $transition-slow;
    background-size: cover;
    background-position: center;
    overflow: hidden;
    min-height: 50vh; // Mobile

    @include respond-to(md) {
      min-height: 100vh;
      &:hover {
        flex: 1.5; // Expand on hover
      }
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      transition: background $transition-normal;
      z-index: 1;
    }

    &.active::before {
      background: rgba(0,0,0,0.3); // Lighten on active
    }

    &--alvine {
      background-image: url("../assets/images/bg_aatcbr.jpg");
      border-right: 1px solid rgba(255,255,255,0.1);
    }

    &--munk {
      background-image: url("../assets/images/bg_munkyourself.jpg");
    }
  }

  &__content {
    position: relative;
    z-index: 2;
    text-align: center;
    padding: $spacing-xl;
    max-width: 500px;
  }

  .hero-subtitle {
    font-size: 1.5rem;
    margin: $spacing-lg 0;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
  }

  .logo {
    width: 300px;
    height: 180px;
    margin: 0 auto;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    filter: drop-shadow(0 10px 20px rgba(0,0,0,0.5));

    &--alvine { background-image: url("../assets/images/aatcbr.png"); }
    &--munk { background-image: url("../assets/images/mkyf.png"); }
  }

  .scroll-indicator {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    color: $white;
    font-size: 2rem;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity $transition-normal;

    &:hover { opacity: 1; }
  }
}

.brand-section {
  padding: $spacing-xxl 0;
}

.section-header {
  text-align: center;
  margin-bottom: $spacing-xl;

  .logo--medium {
    width: 220px;
    height: 100px;
    margin-bottom: $spacing-md;
  }

  .section-subtitle {
    font-size: 1.2rem;
    color: rgba($white, 0.9);
    text-transform: uppercase;
    letter-spacing: 2px;
  }
}

.content-card {
  max-width: 800px;
  margin: 0 auto $spacing-xxl;
  text-align: center;
  
  .section-description {
    font-size: 1.2rem;
    line-height: 1.8;
  }
}

.chipmunks-wrapper {
  display: flex;
  justify-content: center;
  gap: $spacing-xl;
  margin-bottom: $spacing-xxl;
  flex-wrap: wrap;

  .chipmunk-character {
    text-align: center;
    transition: transform $transition-normal;

    &:hover {
      transform: translateY(-10px);
    }

    .character-image {
      width: 150px;
      height: 150px;
      border-radius: 50%;
      background-color: rgba(255,255,255,0.1);
      margin-bottom: $spacing-md;
      box-shadow: $shadow-lg;
      border: 2px solid rgba(255,255,255,0.2);
    }

    h3 {
      font-size: 1.2rem;
      margin-bottom: 0;
    }
  }
}

.social-links {
  display: flex;
  justify-content: center;
  gap: $spacing-lg;
  margin-bottom: $spacing-xxl;
}

.social-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: $spacing-xl;
  margin-top: $spacing-xl;
}

.social-feed-card {
  padding: 0; // Reset padding for image
  overflow: hidden;
  transition: transform $transition-normal, box-shadow $transition-normal;

  &:hover {
    transform: translateY(-5px);
    box-shadow: $shadow-glow-alvine;
  }

  .card-header {
    padding: $spacing-md;
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    background: rgba(0,0,0,0.2);
    
    i { font-size: 1.2rem; }
    h4 { margin: 0; font-size: 1rem; }
  }

  .card-content {
    position: relative;
    padding-bottom: 100%; // Square aspect ratio
    
    .custom-social-embed {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      
      .embed-thumbnail {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .embed-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.3);
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0;
        transition: opacity $transition-normal;

        .embed-icon {
          font-size: 3rem;
          color: $white;
        }
      }

      &:hover .embed-overlay {
        opacity: 1;
      }
    }
  }
}

// Theme Specific Overrides
.blue-theme {
  .social-feed-card:hover { box-shadow: $shadow-glow-munk; }
  .section-title { color: $secondary-color; }
}

.green-theme {
  .social-feed-card:hover { box-shadow: 0 0 20px rgba($tertiary-color, 0.4); }
  .section-title { color: $tertiary-color; }
}
.brand-section {
  .section-header,
  .content-card,
  .social-links,
  .chipmunk-character,
  .social-feed-card {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.8s ease, transform 0.8s ease;

    &.visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .section-header {
    // Specific overrides if needed
  }

  .section-subtitle {
    font-size: 1.2rem;
    opacity: 0.8;
    margin-top: $spacing-md;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .section-description {
    max-width: 800px;
    margin: 0 auto $spacing-xl;
    text-align: center;
    font-size: 1.3rem;
    line-height: 1.8;
    font-weight: 300;
    color: rgba($white, 0.95);
    letter-spacing: 0.5px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }

  .social-links {
    display: flex;
    justify-content: center;
    gap: $spacing-md;
    margin-bottom: $spacing-xl;
  }

  .section-contact {
    margin-top: $spacing-xxl;
    text-align: center;
    display: flex;
    justify-content: center;
  }
}

.chipmunks-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: $spacing-xl;
  margin: $spacing-xl 0;

  .chipmunk-character {
    text-align: center;
    width: 150px;
    
    // Animation styles handled by the grouped selector above
    
    .character-image {
      width: 150px;
      height: 150px;
      border-radius: 50%;
      margin-bottom: $spacing-sm;
      background-size: cover;
      background-position: center;
      transition: all $transition-normal ease;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);

      &:hover {
        transform: scale(1.05) rotate(5deg);
        box-shadow: 0 15px 25px rgba(0, 0, 0, 0.4);
      }
    }

    h3 {
      font-size: 1.25rem;
      margin: $spacing-sm 0 0;
    }

    &.alvin .character-image {
      background-image: url("../assets/images/aatcbr/alvin.png");
      border: 3px solid $primary-color;
      box-shadow: 0 0 15px rgba($primary-color, 0.5);
    }

    &.simon .character-image {
      background-image: url("../assets/images/aatcbr/simon.png");
      border: 3px solid $secondary-color;
      box-shadow: 0 0 15px rgba($secondary-color, 0.5);
    }

    &.theodore .character-image {
      background-image: url("../assets/images/aatcbr/theo.png");
      border: 3px solid $tertiary-color;
      box-shadow: 0 0 15px rgba($tertiary-color, 0.5);
    }

    &.alvin-global .character-image {
      background-image: url("../assets/images/mysf/alvin.png");
      border: 3px solid $primary-color;
      box-shadow: 0 0 15px rgba($primary-color, 0.5);
    }

    &.simon-global .character-image {
      background-image: url("../assets/images/mysf/simon.png");
      border: 3px solid $secondary-color;
      box-shadow: 0 0 15px rgba($secondary-color, 0.5);
    }

    &.theodore-global .character-image {
      background-image: url("../assets/images/mysf/theo.png");
      border: 3px solid $tertiary-color;
      box-shadow: 0 0 15px rgba($tertiary-color, 0.5);
    }
  }
}

.social-feed-section {
  margin: $spacing-xl auto;
  max-width: 90%;
  padding: 0 $spacing-md;

  @include respond-to(md) {
    max-width: 85%;
  }

  .section-title {
    text-align: center;
    font-size: 1.5rem;
    margin-bottom: $spacing-xl;
    position: relative;
    display: inline-block;
    width: 100%;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: 600;

    &::after {
      content: "";
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 2px;
      background-color: currentColor;
      border-radius: 1px;
    }
  }
}

.social-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @include respond-to(sm) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }

  @include respond-to(lg) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.social-feed-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.02);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  opacity: 0;
  transform: translateY(10px);

  &.animate,
  &.animated {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);

    .embed-thumbnail {
      transform: scale(1.05);
    }

    .embed-overlay {
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.8) 0%,
        rgba(0, 0, 0, 0.5) 50%,
        rgba(0, 0, 0, 0.2) 100%
      );
    }
  }

  .card-header {
    padding: $spacing-sm $spacing-md;
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    position: relative;
    z-index: 2;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    i {
      font-size: 1.2rem;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      border-radius: 4px;
      padding: 6px;

      &.fa-instagram {
        background: rgba(253, 89, 73, 0.2);
      }

      &.fa-tiktok {
        background: rgba(37, 244, 238, 0.2);
      }

      &.fa-youtube {
        background: rgba(255, 0, 0, 0.2);
      }
    }

    h4 {
      margin: 0;
      font-size: 0.9rem;
      font-weight: 600;
      color: $white;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
  }
}

.custom-embed-container {
  position: relative;
  padding-bottom: 100%;
  height: 0;
  overflow: hidden;
  width: 100%;
  flex-grow: 1;
}

.custom-social-embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  cursor: pointer;
  background-color: #111;
  text-decoration: none;

  .embed-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .embed-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.1) 100%
    );

    .embed-icon {
      position: absolute;
      top: 10px;
      right: 10px;
      font-size: 1.2rem;
      color: white;
    }

    .embed-caption {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      background: rgba(0, 0, 0, 0.6);
      color: white;
      padding: 10px;
      font-size: 0.85rem;
      font-weight: 500;
    }

    .play-button {
      display: none;
    }
  }
}

.loading-container {
  text-align: center;
  padding: $spacing-xl 0;

  .loading-spinner {
    display: inline-block;
    width: 50px;
    height: 50px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top-color: $primary-color;
    animation: spin 1s ease-in-out infinite;
    margin-bottom: $spacing-md;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
